import { useState, useEffect, useCallback } from "react";

import { getVibechatMetrics } from "@/common/services/leads.api";

import { DashboardHeader, FilterModal } from "@/pages/Dashboard/Leads/components";

import Loading from "@/common/components/ui/loading/Loading";
import TableFooter from "@/common/components/ui/table/TableFooter";
import GenericTable from "@/common/components/ui/table/GenericTable";

import { useLeadFilters } from "../leads/hooks/useLeadFilters";
import { VibechatMetricResponse } from "@/types/lead.type";

const tablePendingMetricsHeaders = {
  teamName: "Nome do Time",
  conversationNotResolved: "Conversas não atendidas",
  timeConversationNotResolved: "Tempo Médio Não Atendidas",
};

const tableHeadersMetrics = {
  teamName: "Nome do Time",
  averageServiceTime: "Tempo Médio de Serviço",
  attendedConversations: "Atendidos",
  unfinishedConversations: "Não atendidos",
  totalConversations: "Total de Conversas",
};

const tableConsultantsHeaders = {
  name: "Nome do Consultor",
  attendedConversations: "Atendidos",
  unfinishedConversations: "Não atendidos",
  totalConversations: "Total de Conversas",
  avgResolutionTime: "Tempo Médio de Resolução",
  avgFirstResponseTime: "Tempo Médio da Primeira Resposta",
  avgReplyTime: "Tempo Médio de Resposta",
};

function convertSecondsToTimeString(totalSeconds: number): string {
  const baseCalc = 3600; // 1 hour in seconds

  const days = Math.floor(totalSeconds / (baseCalc * 24));

  totalSeconds -= days * baseCalc * 24;

  const hours = Math.floor(totalSeconds / baseCalc);
  const minutes = Math.floor((totalSeconds % baseCalc) / 60);
  const seconds = totalSeconds % 60;

  const daysStr = days > 0 ? `${days}d ` : "";
  const hoursStr = hours > 0 ? `${hours}h ` : "";
  const minutesStr = minutes > 0 ? `${minutes}m ` : "";
  const secondsStr = `${Math.round(seconds)}s`;

  return `${daysStr}${hoursStr}${minutesStr}${secondsStr}`.trim();
}

function formatGridList(metrics: VibechatMetricResponse[]) {
  const teams: {
    teamName: string;
    averageServiceTime: string;
    attendedConversations: number;
    unfinishedConversations: number;
    totalConversations: number;
  }[] = []
  const consultants: {
    name: string;
    attendedConversations: number;
    unfinishedConversations: number;
    totalConversations: number;
    avgResolutionTime: string;
    avgFirstResponseTime: string;
    avgReplyTime: string;
  }[] = []

  for (const metric of metrics) {
    const agentesByTeam = metric.agents || [];

    const teamName = formatCityName(metric.team_name || "Sem Nome");

    let teamAttendedConversations = 0;
    let teamUnfinishedConversations = 0;

    for (const agent of agentesByTeam) {
      teamAttendedConversations += agent.attended_conversations;
      teamUnfinishedConversations += agent.unfinished_conversations;
    }

    teams.push({
      teamName: teamName,
      averageServiceTime: convertSecondsToTimeString(metric.average_service_time),
      attendedConversations: teamAttendedConversations,
      unfinishedConversations: teamUnfinishedConversations,
      totalConversations: teamAttendedConversations + teamUnfinishedConversations,
    });

    for (const agent of agentesByTeam) {
      const existsingConsultant = consultants.find((c) => c.name === agent.agent_name);

      if (existsingConsultant) {
        existsingConsultant.attendedConversations += agent.attended_conversations;
        existsingConsultant.unfinishedConversations += agent.unfinished_conversations;
        existsingConsultant.totalConversations += agent.attended_conversations + agent.unfinished_conversations;

        continue;
      }

      consultants.push({
        name: agent.agent_name,
        attendedConversations: agent.attended_conversations,
        unfinishedConversations: agent.unfinished_conversations,
        totalConversations: agent.attended_conversations + agent.unfinished_conversations,
        avgResolutionTime: convertSecondsToTimeString(agent.avg_resolution_time),
        avgFirstResponseTime: convertSecondsToTimeString(agent.avg_first_response_time),
        avgReplyTime: convertSecondsToTimeString(agent.avg_reply_time),
      });
    }
  }

  return { teams, consultants };
}

function formatGridPendingList(metrics: VibechatMetricResponse[]) {
  const teams: {
    teamName: string;
    conversationNotResolved: number;
    timeConversationNotResolved: string;
  }[] = []

  for (const metric of metrics) {
    const agentesByTeam = metric.agents || [];
    const teamName = formatCityName(metric.team_name || "Sem Nome");

    let teamUnfinishedConversations = 0;
    let totalAvgTime = 0;
    let agentsWithTime = 0;

    for (const agent of agentesByTeam) {
      teamUnfinishedConversations += agent.unfinished_conversations;

      // Calcula tempo médio de conversas não atendidas
      const avgTime = agent.avg_first_response_time ?? agent.avg_reply_time ?? 0;
      if (avgTime > 0) {
        totalAvgTime += avgTime;
        agentsWithTime++;
      }
    }

    // Média do tempo entre agentes com dados
    const avgPendingTime = agentsWithTime > 0 ? totalAvgTime / agentsWithTime : 0;

    teams.push({
      teamName: teamName,
      conversationNotResolved: teamUnfinishedConversations,
      timeConversationNotResolved: convertSecondsToTimeString(avgPendingTime), // função para formatar segundos
    });
  }

  return { teams };
}


function formatCityName(city: string): string {
  const cleaned = city.replace(/^comercial\s*\|\s*/i, '').trim();

  return cleaned
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default function ChatService() {
  const OpenrModal = () => setModal(true);
  const CloseModal = () => setModal(false);

  const { filters, setFilters } = useLeadFilters()

  const [modal, setModal] = useState<boolean>(false);
  const [metrics, setMetrics] = useState<VibechatMetricResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const { start, end } = filters.dateRange
      const { data } = await getVibechatMetrics(start, end);

      setMetrics(data || []);

    } catch (error) {
      console.error("Erro ao carregar pending alerts:", error);
    } finally {
      setIsLoading(false);
    }
  }, [filters.dateRange]);

  useEffect(() => { fetchData() }, [fetchData]);

  const gridTeamsData = formatGridList(metrics).teams;
  const gridPendingData = formatGridPendingList(metrics).teams;
  const gridConsultantsData = formatGridList(metrics).consultants;

  return (
    <>
      <DashboardHeader
        companyName="OPERAÇÃO BRASIL"
        onOpenFilter={OpenrModal}
        isRefreshing={false}
        refetch={fetchData}
        title="Dashboard Atendimento"
      />
      <FilterModal
        isOpen={modal}
        onClose={CloseModal}
        onApply={fetchData}
      />

      {isLoading
        ? (<Loading />)
        : (
          <div>
            <div className="table-section">
              <span className="text-lg font-semibold mb-4 block">
                Atendimentos Pendentes
              </span>

              <GenericTable
                headers={tablePendingMetricsHeaders}
                data={gridPendingData}
                setFilters={({ orderBy }) => {
                  setFilters({ dateRange: filters.dateRange, orderBy });
                }}
                entity="leads"
              />

              <TableFooter
                page={1}
                totalItems={gridTeamsData.length}
                itemsPerPage={gridTeamsData.length}
                goToPage={() => { }}
              />
            </div>

            <div className="table-section">
              <span className="text-lg font-semibold mb-4 block">
                Atendimentos por Equipe
              </span>

              <GenericTable
                headers={tableHeadersMetrics}
                data={gridTeamsData}
                setFilters={({ orderBy }) => {
                  setFilters({ dateRange: filters.dateRange, orderBy });
                }}
                entity="leads"
              />

              <TableFooter
                page={1}
                totalItems={gridTeamsData.length}
                itemsPerPage={gridTeamsData.length}
                goToPage={() => { }}
              />
            </div>

            <div className="table-section">
              <span className="text-lg font-semibold mb-4 block">
                Atendimentos por Consultor
              </span>
              <GenericTable
                headers={tableConsultantsHeaders}
                data={gridConsultantsData}
                setFilters={({ orderBy }) => {
                  setFilters({ dateRange: filters.dateRange, orderBy });
                }}
                entity="leads"
              />

              <TableFooter
                page={1}
                totalItems={gridConsultantsData.length}
                itemsPerPage={gridConsultantsData.length}
                goToPage={() => { }}
              />
            </div>
          </div>
        )}
    </>
  );
}
